import React, { useState, useEffect } from 'react';
import { X as XIcon, ChevronDown, HelpCircle } from 'lucide-react';
import './HelpModal.css';

const helpSections = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    content: (
      <>
        <p>KRIME lets you explore crime statistics for any city, county, metro, state, division, region, or the entire nation — all in one place.</p>
        <ol>
        <li><strong>Set your default location</strong> when you first sign in — this is the location that loads automatically each time you log in. You can change it in your Profile.</li>
          <li><strong>Search for a location</strong> using the search bar at the top of the screen. Start typing a name and select from the dropdown results.</li>
          <li><strong>Explore the data</strong> using the three views: <em>Data (Tables)</em>, <em>Charts</em>, and <em>Map</em>.</li>
        </ol>
      </>
    )
  },
  {
    id: 'searching',
    title: 'Searching Locations',
    content: (
      <>
        <p>The search bar accepts cities, counties, metros, states, divisions, regions, and the nation. Results are grouped by location type.</p>
        <ul>
          <li>Type at least one character to see suggestions.</li>
          <li>Scroll and click, or use the arrow keys to navigate results and Enter, to select a location.</li>
          <li>Click the X in the search bar, or press Escape, to clear the search.</li>
          <li>For cities, counties, and metros, you can include a state name or abbreviation to narrow results (e.g. "Springfield, IL").</li>
        </ul>
      </>
    )
  },
  {
    id: 'filters',
    title: 'Data Filters',
    content: (
      <>
        <p>Use the Filters to determine the specific data that you see.</p>
        <h4><em>Crime Type</em></h4>
        <p>Choose from All Crime, Violent Crime, or Property Crime categories, or individual offenses like Murder, Robbery, Burglary, and more. These are the index crimes used by the FBI in the Uniform Crime Reporting Program.</p>
        <h4><em>Date Range</em></h4>
        <p>Select a specific year to view the crime statistics. The Free plan includes the most recent year; paid plans unlock the full historical archive.</p>
        <h4><em>Data Source</em></h4>
        <ul>
          <li><strong>Local</strong> — Crime statistics for the selected location, often directly from the corresponding police department.</li>
          <li><strong>FBI (CIUS)</strong> — Crime in the United States. Annual summary data from the Federal Bureau of Investigation.</li>
          <li><strong>BJS (NCVS)</strong> — National Crime Victimization Survey. Annual survey-based estimates from the Bureau of Justice Statistics.</li>
        </ul>
        <p>You can remove the Filters panel and just use the Filters menu by selecting the Filters icon in the search bar and toggling off the 'Show Filters on Screen' button.</p>
      </>
    )
  },
  {
    id: 'tabs-views',
    title: 'Analysis Tabs',
    content: (
      <>
        <p>The Analysis Tabs allow you to view the crime statistics in different ways.</p>
        <ul>
          <li><strong>Reports</strong> — Comprehensive crime reports with detailed breakdowns of incident totals, historical trends, rate comparisons, and composite scores.</li>
          <li><strong>Totals</strong> — Total crime counts and averages for the selected location.</li>
          <li><strong>Trends</strong> — Crime patterns over time used to determine the changing nature of crime for a particular location.</li>
          <li><strong>Rates</strong> — Crime rates per-capita (per 100,000 people) used to understand risk relative to population sizes.</li>
          <li><strong>Scores</strong> — KRIME Scores™ used to rank locations based on weighted metrics for each type of violent or property crime.</li>
        </ul>
      </>
    )
  },
  {
    id: 'krime-scores',
    title: 'KRIME Scores™',
    content: (
      <>
        <p>Standardized 0–100 index that makes it easy to compare crime across locations — the lower the score, the more crime relative to other locations.</p>
        <ul>
          <li>Composite KRIME Scores™ are calculated from weighted individual crimes only: 
            <ul>
              <p></p>
              <li><strong>Murder - </strong> 30%</li>
              <li><strong>Rape - </strong> 20%</li>
              <li><strong>Robbery - </strong> 15%</li>
              <li><strong>Aggravated Assault - </strong> 15%</li>
              <li><strong>Burglary - </strong> 10%</li>
              <li><strong>Larceny/Theft - </strong> 5%</li>
              <li><strong>Motor Vehicle Theft - </strong> 5%</li>
            </ul>
          </li>
          <li>Aggregate weights represent the sum of individual crime weights within each category:
            <ul>
              <p></p>
              <li><strong>All Crime - </strong> 100%</li>
              <li><strong>Violent Crime - </strong> 80%</li>
              <li><strong>Property Crime - </strong> 20%</li>
            </ul>
          </li>
          <li>Scores are relative to the comparison group shown (e.g., all cities in a state or all counties nationwide).</li>
        </ul>
      </>
    )
  },
  {
    id: 'view-types',
    title: 'View Types',
    content: (
      <>
        <p>The sections used throughout the app to visualize crime data.</p>
        <ul>
          <li><strong>Data</strong> — Sortable data tables with detailed breakdowns by crime type (speciifc location), location (all locations of a specific type), or year (historical data).</li>
          <li><strong>Charts</strong> — Visual bar charts, area charts, line charts, and guages with tooltips for comparisons or historical trends.</li>
          <li><strong>Map</strong> — Color-coded geographic map for the different types of analysis. Legend provides corresponding value ranges. There are also multiple map buttons to switch between different metrics, locations, and map types.</li>
        </ul>
      </>
    )
  },
  {
    id: 'saved-searches',
    title: 'Saved Searches',
    content: (
      <>
        <p>Save your current search (Location + Crime Type + Date Range + Data Source) so you can quickly return to it later.</p>
        <ul>
          <li>Select the <strong>Saved Search</strong> button in the Filters panel or menu. </li>
          <li>To load a Saved Search, open the <strong>Saved</strong> button with the star icon in the header or mobile navigation menu in the footer and selecting Apply next to the saved search you want to load.</li>
          <li>Delete a saved search by clicking the trash icon next to it in the Saved Searches panel.</li>
        </ul>
        <p><em>Saved Searches are only available to paid plans.</em></p>
      </>
    )
  },
  {
    id: 'alerts',
    title: 'Alerts & Notifications',
    content: (
      <>
        <p>Set up alerts to be notified when crime data is updated for locations you select.</p>
        <ul>
          <li>Select the <strong>Set Alert</strong> button next the location name.</li>
          <li>Choose the <strong>crime type(s)</strong> you want to monitor (can be multiple).</li>
          <li>Pick a <strong>frequency</strong>: Daily, Weekly, Monthly, or Yearly.</li>
          <li>Choose to receive notifications <strong>In-App</strong>, via <strong>Email</strong>, or both.</li>
        </ul>
        <p>Unread notifications appear as a badge on the Alerts icon. Open the Alerts panel to view your notifications and manage your alerts.</p>
        <p><em>Alerts are only available to paid plans.</em></p>
      </>
    )
  },
  {
    id: 'plans',
    title: 'Plans & Usage Limits',
    content: (
      <>
        <p>KRIME offers four plans to fit your needs:</p>
        <table className="help-plans-table">
          <thead>
            <tr>
              <th><em>Feature</em></th>
              <th>Free</th>
              <th>Lieutenant</th>
              <th>Captain</th>
              <th>Chief</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Location searches</td>
              <td>5 to get started</td>
              <td>50 / month</td>
              <td>200 / month</td>
              <td>Unlimited</td>
            </tr>
            <tr>
              <td>Active Alerts</td>
              <td>—</td>
              <td>10</td>
              <td>20</td>
              <td>Unlimited</td>
            </tr>
            <tr>
              <td>Historical Data</td>
              <td>Latest year</td>
              <td colSpan="3">Full archive</td>
            </tr>
            <tr>
              <td>Saved Searches</td>
              <td>—</td>
              <td colSpan="3">Included</td>
            </tr>
          </tbody>
        </table>
        <p>Manage your subscription plan from your <strong>Profile → Subscription</strong>.</p>
      </>
    )
  },
  {
    id: 'profile',
    title: 'Your Profile',
    content: (
      <>
        <p>Access your Profile from the top-right navigation (desktop) or the user icon next to the search bar (mobile).</p>
        <ul>
          <li><strong>Account Information</strong> — Edit your name, view your email address/username, log in method, sign up date, and plan usage.</li>
          <li><strong>Default Settings</strong> — Set your default location, crime type, and data view.</li>
          <li><strong>Subscription Plan</strong> — View your current plan, other plans, and upgrade options.</li>
          <li><strong>Search History</strong> — See your recent searches and how many you've used this month.</li>
          <li>Log Out of your account to end your session.</li>
        </ul>
      </>
    )
  },
  {
    id: 'tips',
    title: 'Tips & Shortcuts',
    content: (
      <>
        <ul>
          <li><strong>Click column headers</strong> in tables to sort by that column.</li>
          <li><strong>Hover over map regions</strong> to see more details for that location.</li>
          <li><strong>Use the map legend</strong> to adjust view ranges of all locations statewide or nationwide.</li>
          <li><strong>Keyboard navigation</strong> in search: Arrow keys to move, Enter to select, Escape to clear.</li>
        </ul>
      </>
    )
  }
];

const HelpModal = ({ isOpen, onClose }) => {
  const [expandedSection, setExpandedSection] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setExpandedSection(null);
      return;
    }
    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      onClose();
      setTimeout(() => document.activeElement?.blur(), 0);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const toggleSection = (id) => {
    setExpandedSection(prev => prev === id ? null : id);
  };

  if (!isOpen) return null;

  return (
    <div className="help-modal-overlay" onClick={onClose}>
      <div className="help-modal" onClick={(e) => e.stopPropagation()}>
        <button className="help-modal-close" onClick={onClose}>
          <XIcon size={24} />
        </button>

        <div className="help-modal-header">
          <div className="help-modal-icon-wrap">
            <HelpCircle size={32} />
          </div>
          <h2>Help Center</h2>
          <p className="help-modal-subtitle">Expand each section below to learn more</p>
        </div>

        <div className="help-modal-content">
          {helpSections.map((section) => (
            <div 
              key={section.id} 
              className={`help-section ${expandedSection === section.id ? 'expanded' : ''}`}
            >
              <button 
                className="help-section-toggle"
                onClick={() => toggleSection(section.id)}
              >
                <span className="help-section-title">{section.title}</span>
                <ChevronDown size={18} className="help-section-chevron" />
              </button>
              <div className="help-section-body">
                <div className="help-section-inner">
                  {section.content}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="help-modal-footer">
          <p>Still have questions? Contact us at <a href="mailto:support@krime.app">support@krime.app</a></p>
        </div>
      </div>
    </div>
  );
};

export default HelpModal;
